<template>
  <div class="approvals-flow-detail-pending px-8 pt-6 pb-6">
    <ApprovalsPanelBackButton
      class="mb-6"
      @click="viewOtherApprovals"
    />

    <ApprovalFlowStartedBanner
      v-if="initialShowStartedBanner && activeFlow?.status === 'pending'"
      :job-title="jobProfileTitle"
      :current-stage="activeFlow?.current_stage ?? 1"
      class="mb-6"
      @close="clearStartedBanner"
    />

    <div class="text-h1 text-textDarkest mb-6">
      {{ pageTitle }}
    </div>

    <ApprovalFlowStagesView
      :flow="activeFlow"
      wrapper-class="mb-6"
    />

    <div
      v-if="activeFlow?.status === 'pending' && activeFlow?.canEdit"
      class="pending-flow-actions w-100"
    >
      <DpplNewButton
        color="primary"
        height="40"
        @click="handleOpenFlowEdit"
      >
        Edit Approval Flow
      </DpplNewButton>

      <DpplNewButton
        variant="outlined"
        height="40"
        @click="showCancelModal = true"
      >
        Cancel Approval Flow
      </DpplNewButton>
    </div>

    <CancelApprovalFlowModal
      :show="showCancelModal"
      :job-profile-title="jobProfileTitle"
      :loading="isCancelling"
      @close="showCancelModal = false"
      @cancel="handleCancelApprovalFlow"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import ApprovalFlowStartedBanner from '@/components/jobProfiles/editor/approvals/ApprovalFlowStartedBanner.vue';
import ApprovalFlowStagesView from '@/components/jobProfiles/editor/approvals/ApprovalFlowStagesView.vue';
import ApprovalsPanelBackButton from '@/components/jobProfiles/editor/approvals/ApprovalsPanelBackButton.vue';
import CancelApprovalFlowModal from '@/components/jobProfiles/editor/approvals/CancelApprovalFlowModal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useTracking } from '@/stores/tracking/tracking';

defineProps({
  initialShowStartedBanner: {
    type: Boolean,
    default: false,
  },
});

const { jobProfile } = storeToRefs(useCurrentJobProfile());
const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

const approvalFlowsStore = useApprovalFlows();
const {
  activeFlow,
  isCancelling,
} = storeToRefs(approvalFlowsStore);
const {
  viewOtherApprovals,
  clearStartedBanner,
  cancelActiveFlow,
  openFlowEdit,
} = approvalFlowsStore;

const showCancelModal = ref(false);

const jobProfileTitle = computed(() => jobProfile.value?.title ?? 'job profile');

const pageTitle = computed(() => (
  activeFlow.value?.is_current ? 'Current Approval Flow' : 'Approval Flow'
));

const handleOpenFlowEdit = () => {
  useTracking().approvalPanelOpenedEditFlow();

  openFlowEdit();
};

const handleCancelApprovalFlow = async () => {
  try {
    await cancelActiveFlow();

    useTracking().approvalPanelCancelledFlow({ location: 'Editor Panel' });

    showCancelModal.value = false;

    addSuccessSnackbar({
      text: 'Approval flow cancelled',
    });
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to cancel approval flow — ${error.message}`,
    });

    throw error;
  }
};
</script>

<style scoped lang="less">
.pending-flow-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 1399px) {
    grid-template-columns: 1fr;
  }

  :deep(.v-btn) {
    width: 100%;
  }
}
</style>
